import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { cn } from "@/lib/utils";
import { ScrollHint } from "@/components/ui/scroll-hint";
import { useDragScroll } from "@/hooks/useDragScroll";

interface RecentPurchase {
  id: string;
  name: string;
  image: string;
  buyer: string;
  location: string;
  timeAgo: string;
}

interface RecentlyPurchasedProps {
  purchases: RecentPurchase[];
}

export const RecentlyPurchased = ({ purchases }: RecentlyPurchasedProps) => {
  const [isPaused, setIsPaused] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const scrollPositionRef = useRef(0);

  const { scrollRef, isDragging, containerProps } = useDragScroll({
    speed: 1.5,
    onDragStart: () => setIsPaused(true),
    onDragEnd: () => setTimeout(() => setIsPaused(false), 2000),
  });

  if (!purchases.length) return null;

  // Duplicate items for infinite scroll effect
  const duplicatedPurchases = [...purchases, ...purchases];

  // Infinite marquee animation
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let animationId: number;
    const scrollSpeed = 0.5;

    const animate = () => {
      if (!isPaused && !isDragging && container) {
        const singleSetWidth = container.scrollWidth / 2;
        scrollPositionRef.current += scrollSpeed;
        
        if (scrollPositionRef.current >= singleSetWidth) {
          scrollPositionRef.current = 0;
        }
        
        container.scrollLeft = scrollPositionRef.current;
      } else if (container) {
        // Sync position when paused/dragging
        scrollPositionRef.current = container.scrollLeft;
      }
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isPaused, isDragging, purchases]);

  const handleScroll = () => {
    setShowScrollHint(false);
  };

  return (
    <section className="max-w-[1600px] mx-auto px-4 lg:px-6 py-12">
      <div className="flex flex-col items-center text-center mb-8">
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <ShoppingBag className="w-4 h-4" />
          <span>Naposledy zakúpené</span>
        </div>
        <h2 className="text-xl font-bold text-foreground mb-3">Čo nakupujú ostatní</h2>
        <ScrollHint show={showScrollHint} />
      </div>
      
      <div className="relative pb-2">
        {/* Gradient overlays */}
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

        <div 
          {...containerProps}
          className={cn(
            "flex gap-4 overflow-x-auto py-2",
            isDragging ? "cursor-grabbing" : "cursor-grab"
          )}
          onMouseEnter={() => !isDragging && setIsPaused(true)}
          onMouseLeave={() => {
            containerProps.onMouseLeave();
            if (!isDragging) setIsPaused(false);
          }}
          onScroll={handleScroll}
        >
          {duplicatedPurchases.map((purchase, index) => (
            <Link
              key={`${purchase.id}-${index}`}
              to={`/produkt/${purchase.id}`}
              className={cn(
                "flex items-center gap-4 p-4 rounded-xl bg-secondary/30 transition-all duration-300 group border border-border/50 flex-shrink-0",
                isDragging 
                  ? "scale-[0.98] opacity-80" 
                  : "hover:bg-secondary/50 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-1"
              )}
              style={{ width: 'calc((100% - 48px) / 4)', minWidth: '280px' }}
              draggable={false}
            >
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-secondary flex-shrink-0">
                <img
                  src={purchase.image}
                  alt={purchase.name}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                  {purchase.name}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {purchase.buyer} • {purchase.location}
                </p>
              </div>
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <ShoppingBag className="w-4 h-4 text-primary" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
